import React from 'react';
import {
  Box,
  FormControl,
  FormLabel,
  Input,
  Button,
  Typography
} from '@mui/joy';
import VendorSelect from './VendorSelect';
import ExpiryDateShortcuts from './ExpiryDateShortcuts';
import { calcExpiryFromBaseDate } from './formCommon';

export const calcExpiryFromManufacture = calcExpiryFromBaseDate;

export const today = () => new Date().toISOString().slice(0, 10);

export const emptyMaterialForm = () => ({
  material_name: '',
  brands: '',
  purchase_date: today(),
  quantity: '',
  rate: '',
  manufacture_date: '',
  expiry_date: '',
  vendor_id: ''
});

export const validateMaterialForm = (form) => {
  if (!form.material_name?.trim() || !form.purchase_date || !form.quantity || !form.vendor_id) {
    return 'All required fields must be filled';
  }
  if (form.vendor_id === '__add_new__') {
    return 'Please select a vendor or add one first';
  }
  if (parseInt(form.quantity, 10) <= 0) {
    return 'Quantity must be greater than 0';
  }
  if (form.manufacture_date && form.expiry_date) {
    if (new Date(form.expiry_date) <= new Date(form.manufacture_date)) {
      return 'Expiry date must be later than manufacturing date';
    }
  }
  return null;
};

export const materialFormToPayload = (form) => ({
  material_name: form.material_name.trim(),
  brands: form.brands,
  purchase_date: form.purchase_date,
  quantity: parseInt(form.quantity, 10),
  rate: form.rate !== '' ? form.rate : null,
  manufacture_date: form.manufacture_date || null,
  expiry_date: form.expiry_date || null,
  vendor_id: parseInt(form.vendor_id, 10)
});

const inputSx = {
  '&:hover': { borderColor: 'primary.500' },
  '&:focus-within': { borderColor: 'primary.500' }
};

const MaterialForm = ({
  form,
  onChange,
  onSubmit,
  loading = false,
  vendors = [],
  vendorsLoading = false,
  onAddVendor,
  submitLabel = 'Add Material',
  fullWidthSubmit = true
}) => {
  const handleChange = (e) => {
    onChange(e);
  };

  const handleVendorChange = (vendorId) => {
    onChange({ target: { name: 'vendor_id', value: vendorId } });
  };

  const setExpiryDate = (expiry) => {
    onChange({ target: { name: 'expiry_date', value: expiry } });
  };

  return (
    <form onSubmit={onSubmit}>
      <FormControl sx={{ mb: 2 }}>
        <FormLabel>Material Name</FormLabel>
        <Input
          name="material_name"
          value={form.material_name}
          onChange={handleChange}
          required
          placeholder="Enter material name"
          sx={inputSx}
        />
      </FormControl>

      <FormControl sx={{ mb: 2 }}>
        <FormLabel>Brands (comma separated)</FormLabel>
        <Input
          name="brands"
          value={form.brands}
          onChange={handleChange}
          placeholder="e.g. 3M, Colgate, Dentsply"
          sx={inputSx}
        />
      </FormControl>

      <FormControl sx={{ mb: 2 }}>
        <FormLabel>Purchase Date</FormLabel>
        <Input
          type="date"
          name="purchase_date"
          value={form.purchase_date}
          onChange={handleChange}
          required
          sx={inputSx}
        />
      </FormControl>

      <FormControl sx={{ mb: 2 }}>
        <FormLabel>Quantity</FormLabel>
        <Input
          type="number"
          name="quantity"
          value={form.quantity}
          onChange={handleChange}
          required
          min="1"
          placeholder="Enter quantity"
          sx={inputSx}
        />
      </FormControl>

      <FormControl sx={{ mb: 2 }}>
        <FormLabel>Rate (per item)</FormLabel>
        <Input
          type="number"
          name="rate"
          value={form.rate}
          onChange={handleChange}
          min="0"
          step="0.01"
          inputMode="decimal"
          placeholder="0.00"
          startDecorator={<Typography sx={{ color: 'text.tertiary', fontWeight: 500 }}>₹</Typography>}
          slotProps={{ input: { component: 'input' } }}
          sx={inputSx}
        />
      </FormControl>

      <FormControl sx={{ mb: 1 }}>
        <FormLabel>Manufacturing Date</FormLabel>
        <Input
          type="date"
          name="manufacture_date"
          value={form.manufacture_date}
          onChange={handleChange}
          sx={inputSx}
        />
      </FormControl>

      <ExpiryDateShortcuts
        baseDate={form.manufacture_date}
        onExpiryChange={setExpiryDate}
        hintLabel="Set expiry from manufacturing date"
      />

      <FormControl sx={{ mb: 2 }}>
        <FormLabel>Expiry Date</FormLabel>
        <Input
          type="date"
          name="expiry_date"
          value={form.expiry_date}
          onChange={handleChange}
          sx={inputSx}
        />
      </FormControl>

      <VendorSelect
        vendors={vendors}
        value={form.vendor_id || ''}
        onChange={handleVendorChange}
        onAddNew={onAddVendor}
        loading={vendorsLoading}
        required
      />

      <Box sx={{ display: 'flex', justifyContent: fullWidthSubmit ? 'stretch' : 'flex-end', mt: 1 }}>
        <Button
          type="submit"
          loading={loading}
          fullWidth={fullWidthSubmit}
          sx={{
            backgroundColor: 'primary.500',
            '&:hover': { backgroundColor: 'primary.600' }
          }}
        >
          {submitLabel}
        </Button>
      </Box>
    </form>
  );
};

export default MaterialForm;
